"use client";
import { type ReactNode, useEffect, useState } from "react";

import { Button } from "@/components/base/button";
import { Logger } from "@/utils/logger";
import { CheckIcon } from "../icons/icons";
import { HorizontalContainer } from "../layout/horizontal-container";
import { HorizontalSpacer } from "../layout/horizontal-spacer";
import { VerticalContainer } from "../layout/vertical-container";
import { VerticalSpacer } from "../layout/vertical-spacer";
import { Text } from "../ui/text";

interface CopyToClipboardButtonProps {
  ariaLabel?: string;
  copyConfirmationText?: string;
  icon?: ReactNode;
  textToCopy: string;
  textToDisplay?: string;
}

export const CopyToClipboardButton = ({
  ariaLabel,
  copyConfirmationText,
  icon,
  textToCopy,
  textToDisplay,
}: CopyToClipboardButtonProps) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => setIsCopied(false), 3000);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isCopied]);

  return (
    <>
      <VerticalContainer horizontalPlacement="center">
        <Button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(textToCopy).then(() => {
                setIsCopied(true);
              });
            } catch (error) {
              Logger.error(error);
            }
          }}
          ariaLabel={ariaLabel}
        >
          <HorizontalContainer horizontalPlacement="center" noWrap={true} verticalPlacement="center">
            {icon}
            <VerticalSpacer size={"small"} />
            <Text value={`${textToDisplay || textToCopy}`} weight="bold" />
          </HorizontalContainer>
        </Button>
        <HorizontalContainer verticalPlacement="center" noWrap={true}>
          <VerticalSpacer size="medium" />
          {isCopied ? (
            <>
              <Text color="green" size="small" value={copyConfirmationText || "Kopiert"} />
              <VerticalSpacer size="xx-small" />
              <CheckIcon stripIcon={true} color={"green"} size={"x-small"} />
            </>
          ) : (
            <HorizontalSpacer size="small" />
          )}
        </HorizontalContainer>
      </VerticalContainer>
    </>
  );
};
